/**
 * This module contains navigation guards that are used for ensuring application authorization.
 *
 * The navigation guards are not typed as `NavigationGuard` as Vue's own typings include the `next` callback,
 * which they discourage the use of. However, if a navigation guard defines it, it will expect you to use it.
 * We've opted not to use it, but that means we cannot type our navigation guards as `NavigationGuard`.
 */
import type { CheckoutSws } from '@/auth/sws'
import { useAuthStore, type AuthStore } from '@/stores/auth-store'
import { useSpinnerStore, type SpinnerStore } from '@/stores/spinner-store'
import { inject } from 'vue'
import { type NavigationGuardReturn, type RouteLocationNormalized } from 'vue-router'

type AuthorizationContext = {
  swsSdk: CheckoutSws
  authStore: AuthStore
  spinnerStore: SpinnerStore
}

/**
 * Creates a context which is required for authorization. Make sure this is called synchronously within
 * async functions (always call before `await`), as this function calls `inject` and composables which could return
 * undefined if called asynchrously. Ideally this is the first function which is called within an async funciton.
 *
 * @returns Authorization context
 */
const createAuthContext = (): AuthorizationContext => ({
  swsSdk: inject('$swsSdk') as CheckoutSws,
  authStore: useAuthStore(),
  spinnerStore: useSpinnerStore()
})

/**
 * Checks if the application is authorized, if not, it will begin the authorization process by redirecting
 * to the ID service.
 *
 * Note that redirection is asynchronous and is not guaranteed to execute when this function returns, due to how the
 * browser implements redirection (usually happens on the next JS event loop).
 *
 * @param swsSdk Checkout SWS SDK implementation
 * @param authStore Authorization store
 * @param spinnerStore Spinner store
 * @param redirectUrl Which URL to redirect back to from the ID service if the authorization process is completed.
 * @returns Promise which resolves either if authorization was not necessary or if the authorization process has been
 * initiated
 */
const enforceAuthorization = async (
  { swsSdk, authStore, spinnerStore }: AuthorizationContext,
  redirectUrl: string
) => {
  if (!authStore.isLoggedIn) {
    // Leave the spinner on until the browser redirects to the ID service. This is required because the browser handles
    // redirects via window.location.href asynchronously and has no guarantees about when it will do so.
    spinnerStore.incrementLoadingCount()

    await swsSdk.login(undefined, redirectUrl)
    // There will be a redirect at this point, and the application will not be authorized until we redirect back from
    // the ID service
  }
}

/**
 * Navigation guard that handles redirections back from successful authorizations in the ID service.
 * Does nothing if no query parameters for authorization redirects are present or if the application
 * is already authorized.
 *
 * @param to Target route data
 * @returns `true` (allow the route always), or if there was a 
 */
export const handleAuthorizationRedirect = async (to: RouteLocationNormalized): Promise<NavigationGuardReturn> => {
  const authContext = createAuthContext()
  const { code, state } = to.query
  if (typeof code === 'string' && typeof state === 'string') {
    await authContext.spinnerStore.spinWhile(async () => {
      await authContext.swsSdk.exchangeAuthorizationCode(code, state)
    })
    
    // Replace the current URL with the same route, minus the code and state
    const replacementLocation = {
      ...to,
      replace: true
    }
    // Remove the code and state query parameters. We do this on a copy of the `to` object since these are required to
    // be immutable.
    delete replacementLocation.query.code
    delete replacementLocation.query.state

    return replacementLocation
  }
  // Load the route as normal
  return true
}

/**
 * Navigation guard that ensures the application is authorized before loading the route. If the application is not
 * authorized, the authorization process is started and the browser is redirected to SSO.
 *
 * @param to Target route data
 * @returns `true` (allow the route) if the application was authorized upon entering the route, `false` otherwise.
 */
export const requireAuthorization = async (to: RouteLocationNormalized) => {
  const authContext = createAuthContext()

  const isAuthorized = authContext.authStore.isLoggedIn
  if (!isAuthorized) {
    const targetUrl = (import.meta.env.VITE_APP_BASE_URL as string) + to.fullPath
    await enforceAuthorization(authContext, targetUrl)
  }

  return isAuthorized
}

/**
 * Navigation guard which requires authorization when a `require_auth` query parameter is present and has a value of
 * `'1'`. If the application is not authorized in this case, the browser is redirected to SSO. In all other cases
 * the route will load as normal.
 *
 * @param to Target route data
 * @returns If `require_auth == 1`, will return `true` if app was authorized, `false` otherwise. If `require_auth`
 * was not specified, always returns `true`.
 */
export const optionallyRequireAuthorization = async (to: RouteLocationNormalized) => {
  const authContext = createAuthContext()

  if (to.query['require_auth'] === '1') {
    const isAuthorized = authContext.authStore.isLoggedIn
    if (!isAuthorized) {
      const targetUrl = (import.meta.env.VITE_APP_BASE_URL as string) + to.fullPath
      await enforceAuthorization(authContext, targetUrl)
    }

    // Only load the route if the application was authorized at the time this route guard was invoked
    return isAuthorized
  }
  // Always load the route if `require_auth` was not specified
  return true
}
